import apiClient from './apiClient';

/**
 * Interface for tenant data
 * Uses camelCase to match backend DTO pattern
 */
export interface Tenant {
  tenantId: number;
  name: string;
  domain: string;
  siteName?: string;
  presentsText?: string;
  active: boolean;
  isCurrent?: boolean;
  createdAt: string;
  updatedAt: string;
  // Contact information
  contactEmail?: string;
  contactPhone?: string;
  // Localization
  timezone?: string;
  language?: string;
  // Content
  tagline?: string;
  footerText?: string;
  // External links
  privacyPolicyUrl?: string;
  termsUrl?: string;
  // Analytics
  gaTrackingId?: string;
}

/**
 * Interface for tenant branding data
 * Uses camelCase to match backend DTO pattern
 */
export interface TenantBranding {
  tenantId: number;
  brandingId?: number;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  accentColor: string;
  fontFamily: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  customCss: string | null;
  // UI Style options
  buttonStyle?: 'rounded' | 'square' | 'pill';
  cardStyle?: 'flat' | 'raised' | 'outlined';
  themeMode?: 'light' | 'dark' | 'auto';
  // Additional assets
  heroImageUrl?: string | null;
  bannerImageUrl?: string | null;
  emailLogoUrl?: string | null;
  loginBackgroundUrl?: string | null;
}

/**
 * Interface for updating tenant branding text
 */
export interface TenantBrandingText {
  site_name: string;
  presents_text: string;
}

/**
 * Interface for tenant switch response
 */
export interface TenantSwitchResponse {
  message: string;
  tenantId: number;
  name: string;
  token: string;
}

// Event emitter for tenant changes
type TenantChangeListener = (tenantId: number | null) => void;
const tenantChangeListeners: TenantChangeListener[] = [];

/**
 * Service for tenant-related operations
 */
const tenantService = {
  /**
   * Get all tenants (admin/super-admin only)
   */
  getAllTenants: async (): Promise<Tenant[]> => {
    try {
      const response = await apiClient.get('/api/tenants');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get current tenant information
   */
  getCurrentTenant: async (): Promise<Tenant> => {
    try {
      const response = await apiClient.get('/api/tenants/current');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Switch to a specific tenant (super-admin only)
   */
  switchToTenant: async (tenantId: number): Promise<TenantSwitchResponse> => {
    try {
      console.log(`[TenantService] Switching to tenant ${tenantId}`);
      
      // Check if we're already on the requested tenant
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        try {
          const base64Url = currentToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(window.atob(base64));
          
          // If already on the requested tenant, no need to switch
          if (payload.currentTenantId === tenantId) {
            console.log(`[TenantService] Already on tenant ${tenantId}, not switching`);
            // Still notify listeners for consistency
            tenantChangeListeners.forEach(listener => listener(tenantId));
            return {
              message: 'Already on selected tenant',
              tenantId: tenantId,
              name: '',
              token: currentToken
            };
          }
        } catch (e) {
          console.error('[TenantService] Error parsing token:', e);
          // Continue with the switch if there's an error parsing the token
        }
      }
      
      // Check if we're already in the middle of a reload to prevent infinite loops
      const isReloading = sessionStorage.getItem('tenant_switching');
      if (isReloading) {
        console.log(`[TenantService] Page is already reloading from a tenant switch, skipping additional reload`);
        sessionStorage.removeItem('tenant_switching');
        return { message: 'Switch in progress', tenantId: tenantId, name: '', token: '' };
      }
      
      // Set a flag in sessionStorage to prevent infinite loops
      sessionStorage.setItem('tenant_switching', 'true');
      console.log(`[TenantService] Set tenant_switching flag in sessionStorage`);
      
      // Call the API to switch tenants
      console.log(`[TenantService] Calling API to switch to tenant ${tenantId}`);
      const response = await apiClient.post(`/api/tenants/${tenantId}/set-current`);
      
      // When successful, update the token
      if (response.data?.token) {
        console.log(`[TenantService] Received new token from API for tenant ${tenantId}`);
        
        // Store the new token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Update the token in the API client for future requests
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Notify listeners before reloading
        console.log(`[TenantService] Notifying ${tenantChangeListeners.length} listeners about tenant change`);
        tenantChangeListeners.forEach(listener => listener(tenantId));
        
        // Reload the page to apply the new tenant context
        // This is more reliable than trying to update state throughout the app
        console.log(`[TenantService] Reloading page to apply new tenant context`);
        window.location.reload();
      }
      
      return response.data;
    } catch (error) {
      console.error('[TenantService] Error switching tenant:', error);
      // Clean up the flag in case of error
      sessionStorage.removeItem('tenant_switching');
      throw error;
    }
  },
  
  /**
   * Clear tenant selection (super-admin only)
   */
  clearTenantSelection: async (): Promise<{ message: string; token: string }> => {
    try {
      const response = await apiClient.post('/api/tenants/clear-current');
      
      // When successful, update the token
      if (response.data?.token) {
        // Store the new token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Update the token in the API client for future requests
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Notify listeners
        tenantChangeListeners.forEach(listener => listener(null));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get branding for a specific tenant (admin/super-admin only)
   */
  getTenantBranding: async (tenantId: number): Promise<TenantBranding> => {
    try {
      const response = await apiClient.get(`/api/tenants/${tenantId}/branding`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get branding for the current tenant
   */
  getCurrentTenantBranding: async (): Promise<TenantBranding> => {
    try {
      
      const response = await apiClient.get('/api/tenants/current/branding');
      
      return response.data;
    } catch (error) {
      console.error('[tenantService] Error getting current tenant branding:', error);
      
      // Return default branding if API call fails
      const defaultBranding: TenantBranding = {
        tenantId: 1,
        primaryColor: '#2B3990', // Deep blue
        secondaryColor: '#4CAF50', // Green
        tertiaryColor: '#F39C12', // Amber
        accentColor: '#9C27B0', // Purple
        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
        logoUrl: null,
        faviconUrl: null,
        customCss: null
      };
      
      return defaultBranding;
    }
  },
  
  /**
   * Update tenant branding (admin/super-admin only)
   */
  updateTenantBranding: async (tenantId: number, brandingData: Partial<TenantBranding>): Promise<TenantBranding> => {
    try {
      const response = await apiClient.put(`/api/tenants/${tenantId}/branding`, brandingData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Update tenant branding text (site_name and presents_text)
   */
  updateTenantBrandingText: async (tenantId: number, data: TenantBrandingText): Promise<{ success: boolean; message: string; tenant: Tenant }> => {
    try {
      const response = await apiClient.put(`/api/tenants/${tenantId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Subscribe to tenant change events
   */
  subscribeToTenantChanges: (listener: TenantChangeListener): () => void => {
    tenantChangeListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = tenantChangeListeners.indexOf(listener);
      if (index !== -1) {
        tenantChangeListeners.splice(index, 1);
      }
    };
  }
};

export default tenantService;